(() => {
  const ROOT = '[data-kw-v4-clubsport]';
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  function initStatCounters(root, signal) {
    const section = root.querySelector('.kw-v4-clubsport__stats');
    const counters = [...root.querySelectorAll('[data-kw-v4-stat-counter]')];
    if (!section || !counters.length) return;

    const format = (value) => Math.round(value).toLocaleString('da-DK') + '+';
    const reveal = () => {
      if (section.dataset.kwV4StatsRevealed === 'true') return;
      section.dataset.kwV4StatsRevealed = 'true';

      counters.forEach((counter) => {
        const target = Number.parseInt(counter.dataset.target, 10);
        if (!Number.isFinite(target)) return;
        if (reducedMotion()) {
          counter.textContent = format(target);
          return;
        }

        counter.textContent = '0+';
        let current = 0;
        const increment = Math.ceil(target / 40);
        const timer = window.setInterval(() => {
          if (signal.aborted) {
            window.clearInterval(timer);
            return;
          }
          current += increment;
          if (current >= target) {
            current = target;
            window.clearInterval(timer);
          }
          counter.textContent = format(current);
        }, 30);
      });
    };

    if (reducedMotion() || !('IntersectionObserver' in window)) {
      reveal();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        reveal();
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(section);
    signal.addEventListener('abort', () => observer.disconnect(), { once: true });
  }

  function init(root) {
    if (!root || root.dataset.kwV4Ready === 'true') return;

    const controller = new AbortController();
    const { signal } = controller;
    root._kwV4Controller = controller;

    const rail = root.querySelector('[data-kw-v4-feature-rail]');
    const previous = root.querySelector('[data-kw-v4-rail-prev]');
    const next = root.querySelector('[data-kw-v4-rail-next]');
    const controls = root.querySelector('.kw-v4-clubsport__rail-controls');
    const railCue = root.querySelector('[data-kw-v4-rail-cue]');
    let railFrame = 0;
    const railState = { overflow: null, previousDisabled: null, nextDisabled: null };

    const updateRail = () => {
      railFrame = 0;
      if (!rail || !controls || !previous || !next) return;
      const hasOverflow = rail.scrollWidth > rail.clientWidth + 2;
      const previousDisabled = !hasOverflow || rail.scrollLeft <= 2;
      const nextDisabled = !hasOverflow || rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
      if (railState.overflow !== hasOverflow) {
        controls.hidden = !hasOverflow;
        if (railCue) railCue.hidden = !hasOverflow;
        railState.overflow = hasOverflow;
      }
      if (railState.previousDisabled !== previousDisabled) {
        previous.disabled = previousDisabled;
        railState.previousDisabled = previousDisabled;
      }
      if (railState.nextDisabled !== nextDisabled) {
        next.disabled = nextDisabled;
        railState.nextDisabled = nextDisabled;
      }
    };
    const scheduleRailUpdate = () => {
      if (railFrame) return;
      railFrame = requestAnimationFrame(updateRail);
    };
    const moveRail = (direction) => {
      if (!rail) return;
      rail.scrollBy({
        left: direction * Math.max(220, rail.clientWidth * 0.75),
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    };

    previous?.addEventListener('click', () => moveRail(-1), { signal });
    next?.addEventListener('click', () => moveRail(1), { signal });
    rail?.addEventListener('scroll', scheduleRailUpdate, { passive: true, signal });
    rail?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      moveRail(event.key === 'ArrowLeft' ? -1 : 1);
    }, { signal });
    const railObserver = rail && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(scheduleRailUpdate)
      : null;
    railObserver?.observe(rail);
    signal.addEventListener('abort', () => {
      if (railFrame) cancelAnimationFrame(railFrame);
      railObserver?.disconnect();
    }, { once: true });

    const player = root.querySelector('[data-kw-v4-video-player]');
    const trigger = root.querySelector('[data-kw-v4-video-open]');
    const inlineVideo = root.querySelector('[data-kw-v4-inline-video]');
    const frame = inlineVideo?.querySelector('iframe[data-src]');
    const closeButton = root.querySelector('[data-kw-v4-video-close]');

    const closeVideo = ({ restoreFocus = true } = {}) => {
      if (!player || !trigger || !inlineVideo || !frame || !closeButton) return;
      frame.removeAttribute('src');
      inlineVideo.hidden = true;
      closeButton.hidden = true;
      player.classList.remove('is-playing');
      trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) trigger.focus({ preventScroll: true });
    };

    if (player && trigger && inlineVideo && frame && closeButton) {
      trigger.addEventListener('click', () => {
        if (!frame.getAttribute('src')) frame.setAttribute('src', frame.dataset.src);
        inlineVideo.hidden = false;
        closeButton.hidden = false;
        player.classList.add('is-playing');
        trigger.setAttribute('aria-expanded', 'true');
        requestAnimationFrame(() => closeButton.focus({ preventScroll: true }));
      }, { signal });
      closeButton.addEventListener('click', () => closeVideo(), { signal });
      root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && trigger.getAttribute('aria-expanded') === 'true') closeVideo();
      }, { signal });
    }

    initStatCounters(root, signal);

    root.querySelectorAll('[data-kw-v4-spec-link]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const productSection = root.closest('.product, [data-section-id], shopify-section') || document;
        const target = productSection.querySelector('[data-nc-tech-specs-wrapper]');
        const toggle = target?.querySelector('[data-nc-tech-specs-toggle]');
        if (!target) return;
        event.preventDefault();
        if (toggle?.getAttribute('aria-expanded') === 'false') {
          const expandable = toggle.closest('collapsible-expandable');
          if (typeof expandable?.expand === 'function') {
            expandable.expand();
          } else {
            toggle.click();
          }
        }
        history.replaceState(null, '', '#product-technical-specifications');
        requestAnimationFrame(() => {
          if (!target.isConnected) return;
          target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
          toggle?.focus({ preventScroll: true });
        });
      }, { signal });
    });

    root.dataset.kwV4Ready = 'true';
    scheduleRailUpdate();
  }

  function initAll(scope = document) {
    scope.querySelectorAll(ROOT).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(ROOT).forEach((root) => {
      root._kwV4Controller?.abort();
      delete root._kwV4Controller;
      delete root.dataset.kwV4Ready;
    });
  });
})();
